/**
 * API for tour management
 * Connects with the optimized /api/tours endpoint
 */

import { API_ROOT } from '../../../api-config'

const API_BASE_URL = API_ROOT

/**
 * Basic HTTP client
 */
const httpClient = {
  async request(url, options = {}) {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    }

    // Add authentication token if exists
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const response = await fetch(`${API_BASE_URL}${url}`, config)
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return response.json()
  },

  get(url, params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const fullUrl = queryString ? `${url}?${queryString}` : url
    
    return this.request(fullUrl, {
      method: 'GET'
    })
  },

  post(url, data = {}) {
    return this.request(url, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  },

  put(url, data = {}) {
    return this.request(url, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  },

  delete(url) {
    return this.request(url, {
      method: 'DELETE'
    })
  }
}

/**
 * Tours API
 */
export const tourApi = {
  /**
   * Get tours list
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Results limit
   * @param {number} params.offset - Pagination offset
   * @param {string} params.search - Search term
   * @param {string} params.status - Status filter
   * @param {string} params.dateFrom - Date from
   * @param {string} params.dateTo - Date to
   */
  async getTours(params = {}) {
    try {
      const response = await httpClient.get('/api/tours_pending', params)
      return response
    } catch (error) {
      console.error('Error fetching tours:', error)
      throw error
    }
  },

  /**
   * Get tour statistics
   */
  async getTourStats() {
    try {
      const response = await httpClient.get('/api/tours_pending/stats')
      return response
    } catch (error) {
      console.error('Error fetching tour stats:', error)
      throw error
    }
  },

  /**
   * Get specific tour
   * @param {number} id - Tour ID
   */
  async getTour(id) {
    try {
      const response = await httpClient.get(`/api/tours_pending/${id}`)
      return response
    } catch (error) {
      console.error('Error fetching tour:', error)
      throw error
    }
  },

  /**
   * Process tour
   * @param {number} id - Tour ID
   * @param {Object} data - Processing data
   */
  async processTour(id, data = {}) {
    try {
      const response = await httpClient.put(`/api/tours_pending/${id}/process`, data)
      return response
    } catch (error) {
      console.error('Error processing tour:', error)
      throw error
    }
  },

  /**
   * Calculate tour pricing
   * @param {number} id - Tour ID
   * @param {Object} data - Pricing parameters
   */
  async calculatePricing(id, data = {}) {
    try {
      const response = await httpClient.post(`/api/tours_pending/${id}/pricing`, data)
      return response
    } catch (error) {
      console.error('Error calculating pricing:', error)
      throw error
    }
  },

  /**
   * Validate tour
   * @param {number} id - Tour ID
   * @param {Object} data - Validation data
   */
  async validateTour(id, data = {}) {
    try {
      const response = await httpClient.post(`/api/tours_pending/${id}/validate`, data)
      return response
    } catch (error) {
      console.error('Error validating tour:', error)
      throw error
    }
  },

  /**
   * Cancel tour
   * @param {number} id - Tour ID
   */
  async cancelTour(id) {
    try {
      const response = await httpClient.put(`/api/tours_pending/${id}/cancel`)
      return response
    } catch (error) {
      console.error('Error cancelling tour:', error)
      throw error
    }
  }
}

export default tourApi 


// WEBPACK FOOTER //
// ./src/features/tours/api/tourApi.js